import { cloneDeep } from 'lodash'

import { AskResponse, Citation } from '../../api'

export type ParsedAnswer = {
  citations: Citation[]
  markdownFormatText: string,
  generated_chart: string | null
} | null

export const enumerateCitations = (citations: Citation[]) => {
  const filepathMap = new Map()
  for (const citation of citations) {
    const { filepath } = citation
    let part_i = 1
    if (filepathMap.has(filepath)) {
      part_i = filepathMap.get(filepath) + 1
    }
    filepathMap.set(filepath, part_i)
    citation.part_index = part_i
  }
  return citations
}

export function parseAnswer(answer: AskResponse): ParsedAnswer {
  if (typeof answer.answer !== "string") return null
  let answerText = answer.answer
  const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g)

  const lengthDocN = '[doc'.length

  let filteredCitations = [] as Citation[]
  let citationReindex = 0
  // Two different [docN] chunks can point at the SAME source URL (e.g. an ordinance split
  // into chunks), which otherwise renders the same link twice. Track URL -> reference number
  // so repeats collapse onto one reference instead of showing a duplicate. (CB-0015)
  const sourceToReindex = new Map<string, string>()
  citationLinks?.forEach(link => {
    // Replacing the links/citations with number
    const citationIndex = link.slice(lengthDocN, link.length - 1)
    const citation = cloneDeep(answer.citations[Number(citationIndex) - 1]) as Citation
    if (!citation) return
    // Same doc index already handled (its links were replaced) -> nothing to do.
    if (filteredCitations.find(c => c.id === citationIndex)) return
    // Key on URL ONLY: same URL = a genuine duplicate link. Do NOT fall back to filepath,
    // or legitimate multi-part chunks of one file (shown as "Part 1 / Part 2") would collapse.
    const sourceKey = citation.url || ''
    const existingReindex = sourceKey ? sourceToReindex.get(sourceKey) : undefined
    if (existingReindex) {
      // Already have a reference for this source: point this link at it, add no new entry.
      answerText = answerText.replaceAll(link, ` ^${existingReindex}^ `)
      return
    }
    answerText = answerText.replaceAll(link, ` ^${++citationReindex}^ `)
    citation.id = citationIndex // original doc index to de-dupe
    citation.reindex_id = citationReindex.toString() // reindex from 1 for display
    if (sourceKey) sourceToReindex.set(sourceKey, citation.reindex_id)
    filteredCitations.push(citation)
  })

  filteredCitations = enumerateCitations(filteredCitations)

  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
    generated_chart: answer.generated_chart
  }
}
